import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  createDevice,
  CreateDevicePayload,
  deleteDevice,
  DeviceDetailsResponse,
  DeviceResponse,
  DeviceTableData,
  getAllDevicesTable,
  getDevice,
  getDeviceDetails,
  getPartOfDataAllDevices,
  PartOfDataAllDevicesResponse,
  updateDevice,
  UpdateDevicePayload,
} from '@app/api/device.api'
import type { RootState } from '@app/store/store'

import { resetStore } from '@/store/actions/reset'
import { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

import { deleteAlarmInfoData } from './alarm-slice'
import { deleteInverterData } from './inverter-slice'
import { getProfileData } from './profile-slice'
import { GetStateFn } from './types'

type DeviceState = {
  devicesTableData: Record<string, DeviceTableData>
  devicesTableStatus: Status
  devicesPartData: Record<string, PartOfDataAllDevicesResponse>
  devicesPartStatus: Status
  deviceDetailsData: Record<string, DeviceDetailsResponse>
  deviceDetailsStatusById: Record<string, Status>
  devicePanelData: Record<string, DeviceResponse>
  devicePanelStatusById: Record<string, Status>
  createDeviceStatus: Status
  updateDeviceStatus: Status
  deleteDeviceStatus: Status
}

const initialState: DeviceState = {
  devicesTableData: {},
  devicesTableStatus: 'idle',
  devicesPartData: {},
  devicesPartStatus: 'idle',
  deviceDetailsData: {},
  deviceDetailsStatusById: {},
  devicePanelData: {},
  devicePanelStatusById: {},
  createDeviceStatus: 'idle',
  updateDeviceStatus: 'idle',
  deleteDeviceStatus: 'idle',
}

export const getAllDevicesTableData = createAsyncThunk<DeviceTableData[]>(
  'device/getAllDevicesTable',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllDevicesTable()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getDeviceDetailsData = createAsyncThunk<DeviceDetailsResponse, string>(
  'device/getDeviceDetails',
  async (id, { rejectWithValue }) => {
    try {
      return await getDeviceDetails(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateDeviceData = createAsyncThunk<DeviceDetailsResponse, UpdateDevicePayload>(
  'device/updateDevice',
  async (updateDevicePayload, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await updateDevice(updateDevicePayload)

      refreshProfileIfDeviceAssigned(getState, dispatch, updateDevicePayload.id)
      return response
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createDeviceData = createAsyncThunk<DeviceDetailsResponse, CreateDevicePayload>(
  'device/createDevice',
  async (createDevicePayload, { rejectWithValue }) => {
    try {
      return await createDevice(createDevicePayload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const deleteDeviceData = createAsyncThunk<void, string>(
  'device/deleteDevice',
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await deleteDevice(id)

      refreshProfileIfDeviceAssigned(getState, dispatch, id)
      return response
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getPartOfDataAllDevicesData = createAsyncThunk<PartOfDataAllDevicesResponse[]>(
  'device/getPartOfDataAllDevicesData',
  async (_, { rejectWithValue }) => {
    try {
      return await getPartOfDataAllDevices()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getDeviceData = createAsyncThunk<DeviceResponse, string>(
  'device/getDeviceData',
  async (id, { rejectWithValue }) => {
    try {
      return await getDevice(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const deviceSlice = createSlice({
  name: 'device',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(resetStore, () => initialState)
      .addCase(getAllDevicesTableData.pending, (state) => {
        state.devicesTableStatus = 'loading'
      })
      .addCase(getAllDevicesTableData.rejected, (state) => {
        state.devicesTableStatus = 'failed'
      })
      .addCase(getAllDevicesTableData.fulfilled, (state, action) => {
        state.devicesTableData = Object.fromEntries(action.payload.map((device) => [device.id, device]))
        state.devicesTableStatus = 'succeeded'
      })
      .addCase(getPartOfDataAllDevicesData.pending, (state) => {
        state.devicesPartStatus = 'loading'
      })
      .addCase(getPartOfDataAllDevicesData.rejected, (state) => {
        state.devicesPartStatus = 'failed'
      })
      .addCase(getPartOfDataAllDevicesData.fulfilled, (state, action) => {
        state.devicesPartData = Object.fromEntries(action.payload.map((device) => [device.id, device]))
        state.devicesPartStatus = 'succeeded'
      })
      .addCase(getDeviceDetailsData.pending, (state, action) => {
        state.deviceDetailsStatusById[action.meta.arg] = 'loading'
      })
      .addCase(getDeviceDetailsData.rejected, (state, action) => {
        state.deviceDetailsStatusById[action.meta.arg] = 'failed'
      })
      .addCase(getDeviceDetailsData.fulfilled, (state, action) => {
        state.deviceDetailsData[action.payload.id] = action.payload
        state.deviceDetailsStatusById[action.payload.id] = 'succeeded'
      })
      .addCase(getDeviceData.pending, (state, action) => {
        state.devicePanelStatusById[action.meta.arg] = 'loading'
      })
      .addCase(getDeviceData.rejected, (state, action) => {
        state.devicePanelStatusById[action.meta.arg] = 'failed'
      })
      .addCase(getDeviceData.fulfilled, (state, action) => {
        // Legacy GET /devices/:id nie zwraca `id`; argument trasy pozostaje autorytatywnym kluczem rekordu.
        state.devicePanelData[action.meta.arg] = {
          ...action.payload,
          id: action.meta.arg,
        }
        state.devicePanelStatusById[action.meta.arg] = 'succeeded'
      })
      .addCase(createDeviceData.pending, (state) => {
        state.createDeviceStatus = 'loading'
      })
      .addCase(createDeviceData.rejected, (state) => {
        state.createDeviceStatus = 'failed'
      })
      .addCase(createDeviceData.fulfilled, (state, action) => {
        const device = action.payload

        state.deviceDetailsData[device.id] = device
        state.deviceDetailsStatusById[device.id] = 'succeeded'
        state.devicesPartData[device.id] = toDevicePartData(device)
        state.devicesTableData[device.id] = toDeviceTableData(device)
        state.createDeviceStatus = 'succeeded'
      })
      .addCase(updateDeviceData.pending, (state) => {
        state.updateDeviceStatus = 'loading'
      })
      .addCase(updateDeviceData.rejected, (state) => {
        state.updateDeviceStatus = 'failed'
      })
      .addCase(updateDeviceData.fulfilled, (state, action) => {
        const device = action.payload

        state.deviceDetailsData[device.id] = device
        state.deviceDetailsStatusById[device.id] = 'succeeded'
        state.devicesPartData[device.id] = toDevicePartData(device)
        state.devicesTableData[device.id] = toDeviceTableData(device)
        delete state.devicePanelData[device.id]
        state.devicePanelStatusById[device.id] = 'idle'
        state.updateDeviceStatus = 'succeeded'
      })
      .addCase(deleteDeviceData.pending, (state) => {
        state.deleteDeviceStatus = 'loading'
      })
      .addCase(deleteDeviceData.rejected, (state) => {
        state.deleteDeviceStatus = 'failed'
      })
      .addCase(deleteDeviceData.fulfilled, (state, action) => {
        const id = action.meta.arg

        delete state.devicesTableData[id]
        delete state.devicesPartData[id]
        delete state.deviceDetailsData[id]
        delete state.deviceDetailsStatusById[id]
        delete state.devicePanelData[id]
        delete state.devicePanelStatusById[id]
        state.deleteDeviceStatus = 'succeeded'
      })
      .addCase(deleteInverterData.fulfilled, (state, action) => {
        const deletedInverterId = action.payload.id

        Object.values(state.deviceDetailsData).forEach((device) => {
          device.inverterDeviceConnections = device.inverterDeviceConnections.filter(
            ({ inverterId }) => inverterId !== deletedInverterId,
          )
        })
      })
      .addCase(deleteAlarmInfoData.fulfilled, (state, action) => {
        const deletedAlarmId = action.payload.id

        Object.values(state.deviceDetailsData).forEach((device) => {
          device.alarmDeviceConnections = device.alarmDeviceConnections.filter(
            ({ alarmId }) => alarmId !== deletedAlarmId,
          )
        })
      })
  },
})

export default deviceSlice.reducer

const toDevicePartData = (device: DeviceDetailsResponse): PartOfDataAllDevicesResponse => ({
  id: device.id,
  name: device.name,
  type: device.type,
  serialNumber: device.serialNumber,
})

const toDeviceTableData = (device: DeviceDetailsResponse): DeviceTableData => ({
  id: device.id,
  name: device.name,
  type: device.type,
  category: device.category ?? null,
  serialNumber: device.serialNumber,
  udtNumber: device.udtNumber ?? '',
  description: device.description ?? '',
  status: device.status ?? 'NORMAL',
})

const getProfileIdIfDeviceAssigned = (state: RootState, deviceId: string): string | null => {
  const profile = state.profile?.data

  if (!profile) return null

  const deviceInRoot = profile.devices.some(({ id }) => id === deviceId)
  if (deviceInRoot) return profile.id

  const deviceInFirm = profile.firms.some((firm) => firm.devices.some(({ id }) => id === deviceId))
  return deviceInFirm ? profile.id : null
}

const refreshProfileIfDeviceAssigned = (
  getState: GetStateFn,
  dispatch: (action: ReturnType<typeof getProfileData>) => unknown,
  deviceId: string,
) => {
  const profileId = getProfileIdIfDeviceAssigned(getState() as RootState, deviceId)

  if (profileId) dispatch(getProfileData(profileId))
}
